import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import { useState } from "react";

import { countryName } from "@/modules/dashboard/cards/constants/countries.constants";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime, parseDateInput } from "@/shared/utils/dateTimeHelpers";
import { formatRail } from "@/shared/utils/railDisplay";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { PaymentFee } from "../../types/paymentFees.types";
import { FEE_STATUS_PILL_VARIANT, formatFeeFormula, formatRangeLines, shortId } from "../../utils/feeFormat";
import type { FeeChange, FeeChangeRole } from "../../utils/feeHistory";
import { CorridorIdentity, CountryFlag, DirectionTag } from "./CorridorIdentity";

export interface FeeChangeTimelineProps {
  changes: (FeeChange & { appliesNow?: boolean })[];
  organizationNames: Record<string, string>;
  authorNames: Record<string, string>;
  /** The feed says where each change applies; a corridor's own history does not need to. */
  showScope?: boolean;
  /** Day headers, for the long feed. */
  groupByDay?: boolean;
}

const ROLE_TONE: Record<FeeChangeRole, string> = {
  GENERAL: "bg-foreground",
  SPECIAL: "bg-primary",
  COST: "bg-muted-foreground/60",
};

function dayKey(iso: string): string {
  const date = parseDateInput(iso);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function ScopeLine({ fee }: { fee: PaymentFee }) {
  const { t } = useTranslations("paymentFees");
  if (fee.rail && fee.country && fee.currency && fee.direction) {
    return (
      <CorridorIdentity
        route={{ rail: fee.rail, country: fee.country, currency: fee.currency, direction: fee.direction }}
      />
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      {fee.country && <CountryFlag iso3={fee.country} />}
      <span className="text-sm font-medium">{fee.rail ? formatRail(fee.rail) : t("scope_any_rail")}</span>
      <span className="text-xs text-muted-foreground">
        {fee.country ? countryName(fee.country) : t("scope_any_country")} ·{" "}
        {fee.currency ?? t("scope_any_currency")}
      </span>
      {fee.direction ? (
        <DirectionTag direction={fee.direction} />
      ) : (
        <span className="text-[11px] text-muted-foreground">{t("scope_any_direction")}</span>
      )}
    </span>
  );
}

export function FeeChangeTimeline({
  changes,
  organizationNames,
  authorNames,
  showScope = false,
  groupByDay = false,
}: FeeChangeTimelineProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();

  const [now] = useState(() => Date.now());
  const today = dayKey(new Date(now).toISOString());
  const yesterday = dayKey(new Date(now - 86_400_000).toISOString());
  const dayLabel = (iso: string) => {
    const key = dayKey(iso);
    if (key === today) return t("feed_today");
    if (key === yesterday) return t("feed_yesterday");
    return formatDateTime(iso, { locale: activeLanguage, includeTime: false });
  };
  const timeOf = (iso: string) =>
    parseDateInput(iso).toLocaleTimeString(activeLanguage, { hour: "2-digit", minute: "2-digit" });

  const roleLabel = (fee: PaymentFee, role: FeeChangeRole) =>
    role === "SPECIAL" && fee.organization_id
      ? t("role_special_of", {
          name: organizationNames[fee.organization_id] ?? shortId(fee.organization_id),
        })
      : labels.role[role];

  const author = (fee: PaymentFee) =>
    fee.created_by_user_id
      ? (authorNames[fee.created_by_user_id] ?? shortId(fee.created_by_user_id))
      : t("history_unknown_author");

  const period = (change: FeeChange) => {
    const from = formatDateTime(change.fee.valid_from, { locale: activeLanguage });
    if (change.status === "SCHEDULED") return t("period_starts", { from });
    if (!change.effectiveUntil) return t("period_open", { from });
    return t("period_range", {
      from,
      to: formatDateTime(change.effectiveUntil, { locale: activeLanguage }),
    });
  };

  return (
    <ol className="divide-y divide-border">
      {changes.map((change, index) => {
        const { fee, role } = change;
        const showDay =
          groupByDay && (index === 0 || dayKey(changes[index - 1].fee.created_at) !== dayKey(fee.created_at));
        const zero = change.isZero && role !== "COST";
        const ranges = fee.tiers?.length ? formatRangeLines(fee, activeLanguage, labels.formula) : [];
        const formula = (target: PaymentFee) => formatFeeFormula(target, activeLanguage, labels.formula);

        return (
          <li key={fee.id} className={cn(showDay && index > 0 && "border-t-0")}>
            {showDay && (
              <p
                className={cn(
                  "pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                  index > 0 ? "pt-6" : "pt-1",
                )}
              >
                {dayLabel(fee.created_at)}
              </p>
            )}
            <div
              className={cn(
                "grid grid-cols-[3rem_minmax(0,1fr)] gap-x-4 py-3.5",
                change.appliesNow === false && "opacity-60",
              )}
            >
              <span className="pt-0.5 text-xs tabular-nums text-muted-foreground">
                {timeOf(fee.created_at)}
              </span>

              <div className="min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold">
                    <span aria-hidden="true" className={cn("size-2 rounded-full", ROLE_TONE[role])} />
                    {roleLabel(fee, role)}
                  </span>
                  {showScope && <ScopeLine fee={fee} />}
                  {change.status !== "ACTIVE" && (
                    <ColocaStatusPill variant={FEE_STATUS_PILL_VARIANT[change.status]} size="sm">
                      {labels.status[change.status]}
                    </ColocaStatusPill>
                  )}
                </div>

                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 tabular-nums">
                  {change.previous ? (
                    <>
                      <span className="text-sm text-muted-foreground line-through decoration-muted-foreground/40">
                        {formula(change.previous)}
                      </span>
                      <ArrowRight className="size-3.5 self-center text-muted-foreground" aria-hidden="true" />
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">{labels.trend.NEW}:</span>
                  )}
                  <span className={cn("text-base font-semibold", zero && "text-destructive")}>
                    {formula(fee)}
                  </span>
                  {change.trend === "UP" && (
                    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-success">
                      <ArrowUpRight className="size-3.5" aria-hidden="true" />
                      {labels.trend.UP}
                    </span>
                  )}
                  {change.trend === "DOWN" && !zero && (
                    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-warning">
                      <ArrowDownRight className="size-3.5" aria-hidden="true" />
                      {labels.trend.DOWN}
                    </span>
                  )}
                  {zero && (
                    <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-[11px] font-semibold text-destructive">
                      {t("trend_zero")}
                    </span>
                  )}
                </div>

                {ranges.length > 0 && (
                  <ul className="space-y-0.5 text-xs text-muted-foreground">
                    {ranges.map((line) => (
                      <li key={line.label}>
                        {line.label}: <span className="text-foreground">{line.price}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <p className="text-xs text-muted-foreground">
                  <span className="text-foreground/80">{author(fee)}</span>
                  {fee.change_reason ? <> · “{fee.change_reason}”</> : <> · {t("history_no_reason")}</>}
                  {" · "}
                  {period(change)}
                </p>
                {change.appliesNow === false && (
                  <p className="text-xs italic text-muted-foreground">{t("history_not_applying")}</p>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
